import { Dispatch, ReactElement, SetStateAction } from 'react';
import { Button, Text, makeStyles } from '@fluentui/react-components';
import { useAutofocus } from '@utils';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { paths, v2PageBackground } from '@constants';
import { ChevronDoubleLeft16Regular } from '@fluentui/react-icons';
import { ActionTaken, NewPhishingIcon } from '@utils-global';
import { Accordion, Checkbox } from '@components';
import { useIsV2 } from '@data';
import { ACTION_OPTIONS } from './constants';

const useStyles = makeStyles({
  wrapper: {
    padding: '16px',
  },
  button: {
    marginTop: '16px',
    backgroundColor: '#50BCBD',
    width: '100%',
    padding: '10px',
  },
  v2Wrapper: {
    padding: '20px 30px',
    background: v2PageBackground,
    minHeight: '100vh',
    width: '100%',
  },
  v2TitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  v2Title: {
    color: '#ffffff',
  },
  v2AccordionTitle: {
    color: '#ffffff',
    fontWeight: 'normal',
  },
  v2BackButton: {
    color: '#ffffff',
    padding: '0px',
    justifyContent: 'flex-start',
  },
  v2BodyWrapper: {
    marginTop: '20px',
  },
  v2Accordion: {
    marginTop: '14px',
    borderTop: '1px solid rgba(255, 255, 255, 0.2)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
  },
  v2Checkbox: {
    marginTop: '20px',
  },
  v2ActionCheckbox: {
    marginBottom: '12px',
    backgroundColor: '#1D305A',
    borderRadius: '8px',
    padding: '8px',
  },
});

type ReportProps = {
  handleReportMail: () => void;
  actionsTaken: ActionTaken[];
  setActionsTaken: (actions: ActionTaken[]) => void;
  keepEmail: boolean;
  setKeepEmail: Dispatch<SetStateAction<boolean>>;
};

export const Report = ({
  handleReportMail,
  setActionsTaken,
  actionsTaken,
  keepEmail,
  setKeepEmail,
}: ReportProps): ReactElement => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { targetRef } = useAutofocus();
  const isV2 = useIsV2();

  const handleActionToggle = (action: ActionTaken) => {
    setActionsTaken(
      actionsTaken.includes(action)
        ? actionsTaken.filter((a) => a !== action)
        : [...actionsTaken, action],
    );
  };

  if (isV2) {
    return (
      <section tabIndex={-1} ref={targetRef} className={styles.v2Wrapper} aria-label="Report page">
        <Link to={paths.home}>
          <Button
            appearance="transparent"
            icon={<ChevronDoubleLeft16Regular aria-hidden="true" />}
            className={styles.v2BackButton}
          >
            {t('reportPage.v2.back')}
          </Button>
        </Link>
        <div className={styles.v2BodyWrapper}>
          <div className={styles.v2TitleContainer}>
            <NewPhishingIcon aria-hidden="true" />
            <Text size={500} weight="semibold" role="heading" className={styles.v2Title}>
              {t('reportPage.v2.phishingTitle')}
            </Text>
          </div>

          <Accordion
            title={t('reportPage.v2.accordion_title')}
            activeTitle={t('reportPage.v2.accordion_active_title')}
            titleClassName={styles.v2AccordionTitle}
            className={styles.v2Accordion}
          >
            <div role="group" aria-label={t('reportPage.v2.accordion_title')}>
              {ACTION_OPTIONS.map((option) => (
                <Checkbox
                  key={option.value}
                  checked={actionsTaken.includes(option.value)}
                  onChange={() => handleActionToggle(option.value)}
                  label={t(option.translationKey)}
                  className={styles.v2ActionCheckbox}
                />
              ))}
            </div>
          </Accordion>

          <Checkbox
            checked={keepEmail}
            onChange={setKeepEmail}
            label={t('reportPage.v2.keep_email')}
            className={styles.v2Checkbox}
          />

          <Button
            id="report-button"
            appearance="primary"
            className={styles.button}
            onClick={handleReportMail}
          >
            {t('reportPage.v2.button')}
          </Button>
        </div>
      </section>
    );
  }
  return (
    <section
      tabIndex={-1}
      ref={targetRef}
      className={styles.wrapper}
      aria-describedby="reportPageTitle"
    >
      <Text color="#14141E" size={300} weight="semibold" role="heading" id="reportPageTitle">
        {t('reportPage.main.title')}
      </Text>
      <Button
        id="report-button"
        appearance="primary"
        className={styles.button}
        onClick={handleReportMail}
      >
        {t('reportPage.main.button')}
      </Button>
    </section>
  );
};
